import { router } from "@inertiajs/react";
import { Box, Grid } from "@mui/material";
import ActionCard from "app/frontend/components/ActionCard";
import { useContactSubmissionProjectParams } from "app/frontend/components/contact/useContactSubmissionProjectParams";
import { useIsEligibilityReadOnly } from "app/frontend/components/contact/useIsEligibilityReadOnly";
import BaseEligibilityAccordion from "app/frontend/components/eligibility/accordions/BaseEligibilityAccordion";
import { IEligibilityProps } from "app/frontend/components/eligibility/accordions/types";
import { EEligibilityStep } from "app/frontend/components/eligibility/constants";
import LinkButton from "app/frontend/components/LinkButton";
import { EProjectTypes, PROJECT_TYPE_NAME_BY_ENUM } from "app/frontend/constants/enums";
import { ROUTES } from "app/frontend/constants/routes";
import { useParamsProjectType } from "app/frontend/hooks/project/useParamsProjectType";
import { toId } from "app/frontend/utils/inputs";
import { useCallback, useMemo } from "react";

import LightDutyIcon from "app/frontend/assets/images/mhd/light-duty.svg?react";
import MhdIcon from "app/frontend/assets/images/mhd/public.svg?react";
import MicromobilityIcon from "app/frontend/assets/images/mhd/micro-ebike.svg?react";
import MixedIcon from "app/frontend/assets/images/mhd/mixed.svg?react";

type TProjectTypes = Exclude<EProjectTypes, EProjectTypes.ALL | EProjectTypes.DCFC>;

const CARD_PROJECT_TYPES_BY_TTILE = {
    "Light-Duty": EProjectTypes.L2,
    "Medium- Heavy-Duty": EProjectTypes.MHD,
    Micromobility: EProjectTypes.MICROMOBILITY,
    "Mixed Eligibility": EProjectTypes.MIXED,
} as Record<string, TProjectTypes>;

const NEXT_HEAD_BY_PROJECT_TYPE = {
    [EProjectTypes.L2]: EEligibilityStep.PROJECT_SIZE,
    [EProjectTypes.MHD]: EEligibilityStep.ABOUT,
    [EProjectTypes.MICROMOBILITY]: EEligibilityStep.CONFIRMATION,
    [EProjectTypes.MIXED]: EEligibilityStep.CONFIRMATION,
};

const ProjectTypeEligibilityAccordion: React.FC<IEligibilityProps> = () => {
    const contactParams = useContactSubmissionProjectParams();
    const readOnly = useIsEligibilityReadOnly();

    const projectType = useParamsProjectType();

    const getOnContinue = useCallback(
        (selectedProjectType: TProjectTypes) => () => {
            if (readOnly) return;

            router.visit(ROUTES.Eligibility.ROOT, {
                data: {
                    project_type: selectedProjectType,
                    head: NEXT_HEAD_BY_PROJECT_TYPE[selectedProjectType],
                },
                preserveScroll: true,
            });
        },
        [readOnly],
    );

    const header = useMemo(() => {
        if (!projectType) {
            return "About Your Project";
        } else if (projectType === EProjectTypes.L2) {
            return "Light Duty";
        } else {
            return PROJECT_TYPE_NAME_BY_ENUM[projectType];
        }
    }, [projectType]);

    return (
        <BaseEligibilityAccordion header={header} h2="Tell us about your Project" step={EEligibilityStep.PROJECT_TYPE}>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: { xs: "wrap", lg: "nowrap" },
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: "20px",
                }}
            >
                {CARDS.map((card) => {
                    return (
                        <ActionCard
                            key={toId(card.title)}
                            card={{
                                ...card,
                                disabled: readOnly,
                                sx: {
                                    ...card.sx,
                                    paddingTop: "30px",
                                    border:
                                        readOnly && card.projectType === contactParams?.project_type
                                            ? "3px solid"
                                            : undefined,
                                    borderColor:
                                        readOnly && card.projectType === contactParams?.project_type
                                            ? "primary.light"
                                            : undefined,
                                },
                                action: {
                                    text: "",
                                    onClick: getOnContinue(CARD_PROJECT_TYPES_BY_TTILE[card.title]),
                                },
                            }}
                        />
                    );
                })}
            </Box>

            {!readOnly && (
                <Grid size={12} sx={{ textAlign: "center", marginTop: 3 }}>
                    <LinkButton
                        href={ROUTES.Contact}
                        button={{
                            sx: {
                                textDecoration: "underline",
                            },
                        }}
                    >
                        None of these
                    </LinkButton>
                </Grid>
            )}
        </BaseEligibilityAccordion>
    );
};

const CARDS = [
    {
        title: "Light-Duty" as const,
        alt: "Light-Duty",
        body: "Passenger and commercial vehicles under 10,000 lbs.",
        image: LightDutyIcon,
        imagePosition: "center" as const,
        projectType: EProjectTypes.L2,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },
    {
        title: "Medium- Heavy-Duty" as const,
        alt: "Medium- Heavy-Duty",
        body: "Trucks and buses over 10,000 lbs (Class 3-8).",
        image: MhdIcon,
        imagePosition: "center" as const,
        projectType: EProjectTypes.MHD,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },

    {
        title: "Micromobility" as const,
        alt: "Micromobility.",
        body: "Charging e-bikes or e-scooters on site.",
        image: MicromobilityIcon,
        imagePosition: "center" as const,
        projectType: EProjectTypes.MICROMOBILITY,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },
    {
        title: "Mixed Eligibility" as const,
        alt: "Mixed Projects.",
        body: "Sites servicing a combination of vehicle types (light-duty, medium, heavy-duty, or micromobility).",
        image: MixedIcon,
        imagePosition: "center" as const,
        imageFit: "contain" as const,
        projectType: EProjectTypes.MIXED,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },
];
export default ProjectTypeEligibilityAccordion;
